// Form autosave. Markup contract:
//
//   <form data-autosave data-autosave-status="some-id" ...>
//     <input ...>
//     <p id="some-id" data-autosave-target="status"></p>
//   </form>
//
// Text inputs/textarea: debounced ~700ms after last keystroke.
// File / checkbox / radio / select: submitted immediately on change.
// In-flight requests are aborted when a newer save fires.
// Server should respond to JSON with { ok: true, ... } or 4xx + { errors: [] }.

const DEBOUNCE_MS = 700;
const FLASH_MS = 1800;

function debounce(fn, ms) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), ms);
  };
}

function setStatus(form, text, kind = "info") {
  const el = form.querySelector('[data-autosave-target="status"]');
  if (!el) return;
  el.textContent = text;
  el.dataset.kind = kind;
}

function flashSaved(form) {
  setStatus(form, "Saved", "ok");
  setTimeout(() => {
    const el = form.querySelector('[data-autosave-target="status"]');
    if (el && el.dataset.kind === "ok") setStatus(form, "", "info");
  }, FLASH_MS);
}

function setupAutosaveForm(form) {
  let inflight = null;

  const submit = async () => {
    if (inflight) inflight.abort();
    inflight = new AbortController();
    setStatus(form, "Saving…");

    try {
      const fd = new FormData(form);
      const res = await fetch(form.action, {
        method: "POST", // _method hidden field promotes to PATCH server-side
        body: fd,
        headers: { Accept: "application/json", "X-Requested-With": "XMLHttpRequest" },
        credentials: "same-origin",
        signal: inflight.signal,
      });

      if (res.ok) {
        flashSaved(form);
        form.dispatchEvent(new CustomEvent("autosave:success", { detail: { response: res } }));
      } else {
        let msg = "Couldn't save.";
        try {
          const data = await res.json();
          if (Array.isArray(data.errors) && data.errors.length) msg = data.errors.join(". ");
        } catch (_) {}
        setStatus(form, msg, "error");
      }
    } catch (e) {
      if (e.name === "AbortError") return;
      setStatus(form, "Network error. Try again.", "error");
    } finally {
      inflight = null;
    }
  };

  const debouncedSubmit = debounce(submit, DEBOUNCE_MS);

  form.addEventListener("input", (e) => {
    const t = e.target;
    if (!t || t.tagName === "BUTTON") return;
    const type = (t.type || "").toLowerCase();
    // Text-like inputs are debounced.
    if (t.tagName === "TEXTAREA" || ["text", "email", "search", "url", "tel", "number"].includes(type)) {
      debouncedSubmit();
    }
  });

  form.addEventListener("change", (e) => {
    const t = e.target;
    if (!t) return;
    const type = (t.type || "").toLowerCase();
    if (["file", "checkbox", "radio"].includes(type) || t.tagName === "SELECT") {
      submit();
    }
  });

  // Hide the submit button. Redundant once autosave is wired.
  form.querySelectorAll('[type="submit"]').forEach((b) => (b.hidden = true));

  // Local avatar preview: swap [data-autosave-preview] / fallback visibility.
  const showPreview = (src) => {
    const preview = form.querySelector("[data-autosave-preview]");
    const fallback = form.querySelector("[data-autosave-preview-fallback]");
    if (src) {
      if (preview) {
        preview.src = src;
        preview.classList.remove("hidden");
      }
      if (fallback) fallback.classList.add("hidden");
    } else {
      if (preview) preview.classList.add("hidden");
      if (fallback) fallback.classList.remove("hidden");
    }
  };

  form.querySelectorAll('input[type="file"]').forEach((input) => {
    input.addEventListener("change", () => {
      const file = input.files && input.files[0];
      if (!file) return;
      showPreview(URL.createObjectURL(file));
    });
  });

  form.querySelectorAll('input[type="checkbox"]').forEach((cb) => {
    if (cb.name && cb.name.includes("remove_avatar")) {
      cb.addEventListener("change", () => {
        if (cb.checked) showPreview(null);
      });
    }
  });
}

document.querySelectorAll("[data-autosave]").forEach(setupAutosaveForm);
